/**
 * Session Info Card - Displays comprehensive session information
 * Used across multiple pages for consistent session display
 */

import React, { useState } from 'react';
import { Session, Criterion, Phase, DiaperModel } from '../lib/types';
import { cn, getStatusText, getTimeAgo, generateMACAddresses } from '../lib/utils';
import { toggleAwayStatus, checkoutSession } from '../lib/api-session-actions';
import { replaceClip } from '../lib/api';
import { useSessionsStore } from '../stores/sessionsStore';
import { useToast } from '../stores/toastStore';

interface SessionInfoCardProps {
  session: Session;
  criterion?: Criterion | null;
  phase?: Phase | null;
  changeNeededPercent?: number | null;
  diaperModel?: DiaperModel | null;
  compact?: boolean;
  className?: string;
  onActionComplete?: () => void;
  showActions?: boolean;
  activeSessions?: Session[];
}

export const SessionInfoCard: React.FC<SessionInfoCardProps> = ({
  session,
  criterion,
  phase,
  changeNeededPercent,
  diaperModel,
  compact = false,
  className,
  onActionComplete,
  showActions = false,
  activeSessions = [],
}) => {
  const toast = useToast();
  const currentNovaAccountId = useSessionsStore((state) => state.currentNovaAccountId);
  const [isToggling, setIsToggling] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showChangeClipModal, setShowChangeClipModal] = useState(false);
  const [selectedClipAddress, setSelectedClipAddress] = useState('');
  const [clipName, setClipName] = useState('');
  const [isChangingClip, setIsChangingClip] = useState(false);
  const [showRawJSON, setShowRawJSON] = useState(false);

  const allMACs = generateMACAddresses(150);
  const usedMACs = new Set(activeSessions.map((s) => s.clipAddress?.toUpperCase()));
  const usedMAC = session.clipAddress?.toUpperCase();

  const getWetnessColor = (percent?: number) => {
    if (!percent) return 'text-gray-500';
    if (percent < 15) return 'text-green-600';
    if (percent < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleToggleAway = async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      const newAwayStatus = !session.away;
      await toggleAwayStatus(session.id, newAwayStatus, currentNovaAccountId);
      toast.success(newAwayStatus ? 'Marked as Away' : 'Marked as Present');
      if (onActionComplete) onActionComplete();
    } catch (error: any) {
      toast.error(`Failed to toggle away: ${error.message}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleCheckout = async () => {
    const confirmed = window.confirm(
      `Checkout ${session.client || `Session ${session.id}`}?\n\nThis will end the care period.`
    );
    if (!confirmed) return;
    setIsCheckingOut(true);
    try {
      await checkoutSession(session.id, currentNovaAccountId);
      toast.success('Session checked out successfully');
      if (onActionComplete) onActionComplete();
    } catch (error: any) {
      toast.error(`Failed to checkout: ${error.message}`);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleMACChangeInModal = (mac: string) => {
    setSelectedClipAddress(mac);
    // Find the index of the selected MAC to determine the clip number
    const clipIndex = allMACs.findIndex(m => m === mac);
    if (clipIndex !== -1) {
      const clipNumber = clipIndex + 1;
      // Format as serial number: CLIP-0001, CLIP-0002, etc.
      const serialNumber = `CLIP-${clipNumber.toString().padStart(4, '0')}`;
      setClipName(serialNumber);
    }
  };

  const handleChangeClip = async () => {
    if (!selectedClipAddress) {
      toast.error('Please select a clip');
      return;
    }
    setIsChangingClip(true);
    try {
      const name = clipName || selectedClipAddress;
      await replaceClip(session.id, {
        address: selectedClipAddress.toUpperCase(),
        name,
      }, { novaAccountId: currentNovaAccountId });
      toast.success('Clip changed successfully');
      setShowChangeClipModal(false);
      if (onActionComplete) onActionComplete();
    } catch (error: any) {
      toast.error(`Failed to change clip: ${error.message}`);
    } finally {
      setIsChangingClip(false);
    }
  };

  const handleCopyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(session, null, 2));
    toast.success('Session JSON copied to clipboard!');
  };

  const getDiaperAge = () => {
    if (!session.diaperStartTime) return null;
    const start = new Date(session.diaperStartTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  return (
    <>
    <div
      className={cn(
        'bg-gradient-to-r rounded-lg p-4 shadow-sm',
        !session.monitored 
          ? 'from-gray-100 to-gray-200 border-2 border-gray-400' 
          : session.away
          ? 'from-yellow-50 to-orange-50 border-2 border-yellow-300'
          : 'from-blue-50 to-indigo-50 border-2 border-blue-200',
        compact && 'p-3',
        className
      )}
    >
      {/* Main Session Info Row */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">
              {session.client || `Session ${session.id}`}
            </h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
              ID: {session.id}
            </span>
            {!session.monitored && (
              <span className="px-3 py-1.5 bg-gray-500 text-white text-sm font-bold rounded-lg shadow-md">
                ⏸️ NOT MONITORED
              </span>
            )}
            {session.away && (
              <span className="px-3 py-1.5 bg-yellow-500 text-white text-sm font-bold rounded-lg shadow-md">
                🚶 AWAY
              </span>
            )}
          </div>

          {/* Clip Info */}
          <div className="text-sm space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">Clip:</span>
              <span className="font-mono text-gray-900">
                {session.clipName || 'Unknown'} ({session.clipAddress || 'N/A'})
              </span>
            </div>

            {/* Alert Info - Leak or Change Needed */}
            {(session.leakAlertTime || session.wetAlertTime) && (
              <div className={cn(
                'px-2 py-1.5 rounded-md text-sm font-medium',
                session.leakAlertTime 
                  ? 'bg-red-100 text-red-800 border border-red-200' 
                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              )}>
                <span className="font-semibold">Info:</span>{' '}
                {session.leakAlertTime 
                  ? `🚨 Leak detected ${getTimeAgo(session.leakAlertTime)}`
                  : `⚠️ Change needed ${getTimeAgo(session.wetAlertTime!)}`
                }
              </div>
            )}

            {/* Status Code - For Information Only */}
            {session.statusCode !== undefined && session.statusCode !== 0 && (
              <div className="text-xs text-gray-600">
                Status code: {session.statusCode} ({getStatusText(session.statusCode)})
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex gap-4 text-center">
          <div className="bg-white rounded-lg p-2 min-w-[80px] shadow-sm">
            <div className={cn('text-2xl font-bold', getWetnessColor(session.wetnessPercent))}>
              {session.wetnessPercent?.toFixed(0) || '—'}%
            </div>
            <div className="text-xs text-gray-600 mt-1">Wetness</div>
          </div>

          <div className="bg-white rounded-lg p-2 min-w-[80px] shadow-sm">
            <div
              className={cn(
                'text-2xl font-bold',
                !session.clipBatteryPercent
                  ? 'text-gray-400'
                  : session.clipBatteryPercent < 20
                  ? 'text-red-600'
                  : session.clipBatteryPercent < 50
                  ? 'text-yellow-600'
                  : 'text-green-600'
              )}
            >
              {session.clipBatteryPercent || '—'}%
            </div>
            <div className="text-xs text-gray-600 mt-1">Battery</div>
          </div>

          {session.diaperStartTime && (
            <div className="bg-white rounded-lg p-2 min-w-[80px] shadow-sm">
              <div className="text-2xl font-bold text-purple-600">
                🕐
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {getDiaperAge()}
              </div>
              <div className="text-[10px] text-gray-500">Diaper Age</div>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-blue-200 my-3"></div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-gray-600 font-medium">Diaper Model</div>
          <div className="text-gray-900 font-semibold">
            {diaperModel?.name || session.diaperModelName || 'Unknown'}
          </div>
          {diaperModel && (
            <div className="text-xs text-gray-500">{Math.round(diaperModel.capacity)}mL capacity</div>
          )}
        </div>

        <div>
          <div className="text-gray-600 font-medium">Diaper Counter</div>
          <div className="text-gray-900 font-semibold">
            {session.diaperCounter !== undefined ? session.diaperCounter : '—'}
          </div>
          {session.diaperId && <div className="text-xs text-gray-500">Diaper ID: {session.diaperId}</div>}
        </div>

        <div>
          <div className="text-gray-600 font-medium">Wetness Level</div>
          <div className="text-gray-900 font-semibold font-mono">
            {session.wetnessLevel !== undefined ? session.wetnessLevel : '—'}
          </div>
          <div className="text-xs text-gray-500">Raw sensor value</div>
        </div>

        <div>
          <div className="text-gray-600 font-medium">Firmware</div>
          <div className="text-gray-900 font-semibold">
            v{session.firmwareRevision || '—'}
          </div>
          {session.sessionStartTime && (
            <div className="text-xs text-gray-500">
              Started: {new Date(session.sessionStartTime).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Criterion & Thresholds Section */}
      {criterion && (
        <>
          <div className="border-t border-blue-200 my-3"></div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">Criterion:</span>
              <span className="text-gray-900">{criterion.name}</span>
              <span className="text-xs text-gray-500">(ID: {session.criterionId || criterion.id})</span>
            </div>

            {changeNeededPercent != null && (
              <div className="bg-white/60 rounded-lg p-2 space-y-1">
                <div className="font-medium text-gray-700 text-sm mb-1">Alert Thresholds:</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-600 font-semibold">🟡 Change Needed:</span>
                    <span className="font-bold">{changeNeededPercent.toFixed(1)}%</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <span className="text-blue-600 font-semibold">⏱️ Stabilization:</span>
                    <span className="font-bold">{criterion.content.stabilizationPeriod_sec}s</span>
                  </div>

                  {phase?.thresholds && phase.thresholds[0] && (
                    <div className="flex items-center gap-1">
                      <span className="text-red-600 font-semibold">🔴 Leak Detection:</span>
                      <span className="font-bold">
                        {phase.thresholds[0].leakAdcValuesEnabled ? '✓' : '✗'} ADC,{' '}
                        {phase.thresholds[0].leakLevelEnabled ? '✓' : '✗'} Level,{' '}
                        {phase.thresholds[0].leakClipStatusEnabled ? '✓' : '✗'} Status
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {phase && (
              <div className="text-xs text-gray-600">
                <span className="font-medium">Phase:</span> {phase.name}
              </div>
            )}
          </div>
        </>
      )}

      {/* ADC Values (if available) */}
      {session.adcValues && Array.isArray(session.adcValues) && session.adcValues.length > 0 && (
        <>
          <div className="border-t border-blue-200 my-3"></div>
          <div className="flex items-center gap-2 text-xs">
            <span className="font-medium text-gray-600">ADC Values:</span>
            <span className="font-mono text-gray-700">
              [{session.adcValues.join(', ')}]
            </span>
          </div>
        </>
      )}

      {/* Session Actions */}
      {showActions && (
        <>
          <div className="border-t border-blue-200 my-3"></div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowRawJSON(!showRawJSON)}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              📋 {showRawJSON ? 'Hide' : 'View'} JSON
            </button>

            <button
              onClick={handleToggleAway}
              disabled={isToggling}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                session.away
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } disabled:opacity-50`}
            >
              {isToggling ? '⏳' : session.away ? '🏠 Present' : '🚶 Away'}
            </button>

            <button
              onClick={() => setShowChangeClipModal(true)}
              className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg font-medium transition-colors"
            >
              🔄 Change Clip
            </button>

            <button
              onClick={handleCheckout}
              disabled={isCheckingOut}
              className="px-3 py-1.5 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isCheckingOut ? '⏳' : '🛒 Checkout'}
            </button>
          </div>
        </>
      )}

      {/* Raw JSON Viewer */}
      {showRawJSON && (
        <>
          <div className="border-t border-blue-200 my-3"></div>
          <div className="bg-gray-900 rounded-lg p-4 relative">
            <button
              onClick={handleCopyJSON}
              className="absolute top-2 right-2 px-3 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors"
            >
              📋 Copy
            </button>
            <pre className="text-xs text-green-400 overflow-x-auto pt-8 font-mono">
              {JSON.stringify(session, null, 2)}
            </pre>
          </div>
        </>
      )}
    </div>

    {/* Change Clip Modal */}
    {showChangeClipModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-xl font-bold mb-4">Change Clip for {session.client}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Current Clip</label>
              <input
                type="text"
                value={`${session.clipName || 'Unknown'} (${session.clipAddress || 'N/A'})`}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">New Clip Address</label>
              <select
                value={selectedClipAddress}
                onChange={(e) => handleMACChangeInModal(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select MAC address...</option>
                {allMACs.map((mac, idx) => {
                  const macUpper = mac.toUpperCase();
                  const isCurrentClip = macUpper === usedMAC;
                  const isUsed = usedMACs.has(macUpper) && !isCurrentClip;
                  return (
                    <option key={mac} value={mac} disabled={isCurrentClip || isUsed}>
                      {mac} (Clip {idx + 1}){isCurrentClip ? ' (Current)' : isUsed ? ' ⚠️ In Use' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Clip Name (optional)</label>
              <input
                type="text"
                value={clipName}
                onChange={(e) => setClipName(e.target.value)}
                placeholder="e.g., CLIP-0003"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowChangeClipModal(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleChangeClip}
              disabled={!selectedClipAddress || isChangingClip}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-light font-medium disabled:opacity-50"
            >
              {isChangingClip ? '⏳ Changing...' : 'Change Clip'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};
